import clsx from "clsx";
import { ReactNode, useContext } from "react";
import styled from "styled-components";

import { WidgetFrameTitleContext } from "@/components/layout/WidgetFrame/WidgetFrameTitle/WidgetFrameTitleContext";
import { WidgetFrameTitleInformationIcon } from "@/components/layout/WidgetFrame/WidgetFrameTitle/WidgetFrameTitleInformation/WidgetFrameTitleInformationIcon";
import { WidgetFrameTitleInformationTooltip } from "@/components/layout/WidgetFrame/WidgetFrameTitle/WidgetFrameTitleInformation/WidgetFrameTitleInformationTooltip";
import { WidgetFrameTitleInformationTooltipLastImport } from "@/components/layout/WidgetFrame/WidgetFrameTitle/WidgetFrameTitleInformation/WidgetFrameTitleInformationTooltipLastImport";
import { TOOLTIP_CLASS } from "@/components/layout/WidgetFrame/WidgetFrameTitle/WidgetFrameTitleInformation/widget-frame-title-information.consts";
import { dayjs } from "@/components/ui/DatePicker/date.utils";
import { DataSourceRegistryPath } from "@/features/data-source/data-source-registry";
import { useDataSource } from "@/features/data-source/useDataSource";
import { DocumentationTrigger, useOpenDocumentation } from "@/features/documentation";

const WidgetFrameTitleInformationStyled = styled.div`
    position: relative;

    line-height: 1.5rem;

    & button {
        &:hover {
            & + .${TOOLTIP_CLASS} {
                display: flex;

                opacity: 1;
            }
        }
    }

    @media print {
        display: none;
    }
`;

type LastImportType = "middleware" | "aadpAlarms" | "aadpDataMart";
type Props = {
    children?: ReactNode;
    lastImportType?: LastImportType;
    pageId?: number;
    className?: string;
};

const lastImportTypeDataSourceMap: Record<LastImportType, DataSourceRegistryPath> = {
    middleware: "last-import/middleware",
    aadpAlarms: "last-import/aadp/alarms",
    aadpDataMart: "last-import/aadp/data-mart",
};

const WidgetFrameTitleInformation = (props: Props) => {
    const { children, lastImportType, pageId, className } = props;

    const { title: widgetTitle } = useContext(WidgetFrameTitleContext);

    const lastImportDataSource = lastImportType ? lastImportTypeDataSourceMap[lastImportType] : undefined;
    const { data: lastImport } = useDataSource<[{ timestampUTC: string }]>(
        lastImportDataSource!,
        {},
        {
            skip: !lastImportDataSource,
        },
    );

    const dateFormat = "DD/MM/YYYY HH:mm:ss [(UTC)]";
    const lastImportTime = lastImport?.[0]?.timestampUTC;

    const canShowTooltip = !!children || !!lastImportTime;

    const { openDocumentation } = useOpenDocumentation();
    const handleDocsTriggerClick = () => {
        if (!pageId) return;

        const documentationTitle =
            typeof widgetTitle === "string" || typeof widgetTitle === "number" ? String(widgetTitle) : "";

        openDocumentation({ pageId, title: documentationTitle });
    };

    return (
        <WidgetFrameTitleInformationStyled className={clsx(className)}>
            <DocumentationTrigger onClick={handleDocsTriggerClick}>
                <WidgetFrameTitleInformationIcon icon={"lely-icon-info"} />
            </DocumentationTrigger>
            {canShowTooltip && (
                <WidgetFrameTitleInformationTooltip className={TOOLTIP_CLASS}>
                    {children && <div>{children}</div>}
                    {lastImportTime && (
                        <WidgetFrameTitleInformationTooltipLastImport>
                            Last Updated: {dayjs(lastImportTime).format(dateFormat)}
                        </WidgetFrameTitleInformationTooltipLastImport>
                    )}
                </WidgetFrameTitleInformationTooltip>
            )}
        </WidgetFrameTitleInformationStyled>
    );
};

export { WidgetFrameTitleInformation };
